import type { useTranslations } from '@vocab/react';

import type {
  DashboardDraftFragment,
  DashboardJobFragment,
} from 'src/graphql/generated';
import type { DashboardListItemFragment } from 'src/types';

import {
  getCandidateManagementUrl,
  getContinueDraftUrl,
  getJobAnalyticsUrl,
} from './getUrls';

const getLegacyKind = (
  job: DashboardListItemFragment,
): 'DRAFT' | 'DIRECT_LISTING' | 'INDIRECT_LISTING' => {
  if (isDraftJobItem(job)) {
    return 'DRAFT';
  }

  return job.postType === 'DIRECT' ? 'DIRECT_LISTING' : 'INDIRECT_LISTING';
};

export const getJobKey = (job: DashboardListItemFragment): string => {
  const id = job.id;
  const kind = getLegacyKind(job);
  return `key-${kind}-${id}`;
};

export const getJobTitleLink = (
  job: DashboardListItemFragment,
): { href: string; target?: string } => {
  if (isDraftJobItem(job)) {
    return { href: getContinueDraftUrl(job.id) };
  }

  return { href: getCandidateManagementUrl(job.id) };
};

export const getJobAdRatingDisplayTextAndUrl = (
  job: DashboardListItemFragment,
  t: ReturnType<typeof useTranslations>['t'],
): {
  displayText: string;
  helpText?: string;
  url?: string;
  urlText?: string;
} => {
  const blankResponse = {
    displayText: '-',
    helpText: '',
    url: '',
    urlText: '',
  };

  if (isDraftJobItem(job)) {
    return blankResponse;
  }
  const url = getJobAnalyticsUrl(job.id);

  const performanceRatingLabelMap = {
    ARCHIVED: {
      displayText: t('Rating no longer available'),
      helpText: t('Rating no longer available info'),
      url: '',
      urlText: '',
    },
    AVAILABLE_CANNOT_RATE: {
      displayText: '',
      helpText: '',
      url,
      urlText: t('View stats link'),
    },
    AVAILABLE_PENDING_RATING: {
      displayText: '',
      helpText: '',
      url,
      urlText: t('View stats link'),
    },
    AVAILABLE_WITH_RATING: {
      displayText: '',
      helpText: '',
      url,
    },
    ERRORED: blankResponse,
    NOT_APPLICABLE: blankResponse,
    NOT_INCLUDED: {
      displayText: t('Not included'),
      helpText: t('Not included info'),
      url: '',
      urlText: '',
    },
    PENDING: {
      displayText: t('Available soon'),
      helpText: t('Available soon info'),
      url: '',
      urlText: '',
    },
    UNAVAILABLE: {
      displayText: t('Unavailable'),
      helpText: t('Unavailable info'),
      url: '',
      urlText: '',
    },
    WITHHELD: {
      displayText: '',
      helpText: '',
      url,
      urlText: t('View stats link'),
    },
  };

  const availableRatingLabelMap = {
    HIGH: {
      ...performanceRatingLabelMap.AVAILABLE_WITH_RATING,
      urlText: t('View stats link'),
    },
    NORMAL: {
      ...performanceRatingLabelMap.AVAILABLE_WITH_RATING,
      urlText: t('View stats link'),
    },
    LOW: {
      ...performanceRatingLabelMap.AVAILABLE_WITH_RATING,
      urlText: t('View stats link'),
    },
    ERRORED: performanceRatingLabelMap.ERRORED,
  };

  const performanceRatingStatus = job.performanceRating?.status || 'ERRORED';
  if (performanceRatingStatus === 'AVAILABLE_WITH_RATING') {
    const score = job.performanceRating?.score || 'ERRORED';
    return availableRatingLabelMap[score];
  }
  return performanceRatingLabelMap[performanceRatingStatus];
};

export const isDraftJobItem = (
  job: DashboardListItemFragment,
): job is DashboardDraftFragment => job.__typename === 'DraftJob';

export const getTitle = (item: DashboardListItemFragment) =>
  item.__typename === 'DraftJob' ? item.draftJobTitle ?? '' : item.title;

export const getLocationDescription = (item: DashboardListItemFragment) =>
  isDraftJobItem(item)
    ? item.locationsV2?.[0]?.description
    : item.locations?.[0]?.description;

// useful for candidate count display
export const isPositiveNumber = (
  value: number | null | undefined,
): value is number => typeof value === 'number' && value > 0;

export const isActiveOrExpiredJob = (job: DashboardJobFragment) =>
  ['ACTIVE', 'EXPIRED'].includes(job.status);

export const isIndirectJob = (job: DashboardListItemFragment) =>
  !isDraftJobItem(job) && job.postType === 'INDIRECT';
